import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { SainteCeneReport } from '../../types/sainteCeneReport';

interface Props {
  reports: SainteCeneReport[];
}

type View = 'distribution' | 'taux';
type Period = '1m' | '3m' | '6m' | '1y' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  '1m': '1 mois', '3m': '3 mois', '6m': '6 mois', '1y': '1 an', 'all': 'Tout'
};

function filterByPeriod(reports: SainteCeneReport[], period: Period, from: string, to: string) {
  const now = new Date();
  let start: Date | null = null;
  if (period === '1m') start = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
  else if (period === '3m') start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  else if (period === '6m') start = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  else if (period === '1y') start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
  return reports.filter(r => {
    const d = new Date(r.date);
    if (from) return d >= new Date(from) && (!to || d <= new Date(to));
    if (start) return d >= start!;
    return true;
  }).sort((a, b) => a.date.localeCompare(b.date));
}

export default function SainteCeneTrendChart({ reports }: Props) {
  const [view, setView] = useState<View>('distribution');
  const [period, setPeriod] = useState<Period>('3m');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const filtered = useMemo(() => filterByPeriod(reports, period, from, to), [reports, period, from, to]);

  const chartData = useMemo(() => filtered.map(r => ({
    date: new Date(r.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    painsPrep: r.painsPreparees,
    painsDist: r.painsDistribuees,
    vinsPrep: r.vinsPreparees,
    vinsDist: r.vinsDistribuees,
    tauxPains: r.painsPreparees > 0 ? Math.round((r.painsDistribuees / r.painsPreparees) * 100) : 0,
    tauxVins: r.vinsPreparees > 0 ? Math.round((r.vinsDistribuees / r.vinsPreparees) * 100) : 0,
  })), [filtered]);

  const totPainsDist = filtered.reduce((s, r) => s + r.painsDistribuees, 0);
  const totVinsDist = filtered.reduce((s, r) => s + r.vinsDistribuees, 0);
  const avgTauxPains = chartData.length > 0 ? Math.round(chartData.reduce((s, d) => s + d.tauxPains, 0) / chartData.length) : 0;
  const avgTauxVins = chartData.length > 0 ? Math.round(chartData.reduce((s, d) => s + d.tauxVins, 0) / chartData.length) : 0;

  if (reports.length === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Évolution Sainte Cène</h3>

      {/* Résumé */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-amber-700">{totPainsDist}</div>
          <div className="text-xs text-amber-600">Pains distribués</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-purple-700">{totVinsDist}</div>
          <div className="text-xs text-purple-600">Vins distribués</div>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${avgTauxPains >= 90 ? 'text-green-700' : avgTauxPains >= 70 ? 'text-amber-700' : 'text-red-700'}`}>{avgTauxPains}%</div>
          <div className="text-xs text-blue-600">Taux moy. pains</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className={`text-2xl font-bold ${avgTauxVins >= 90 ? 'text-green-700' : avgTauxVins >= 70 ? 'text-amber-700' : 'text-red-700'}`}>{avgTauxVins}%</div>
          <div className="text-xs text-green-600">Taux moy. vins</div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex gap-2 mb-4">
        {([['distribution', 'Distribution'], ['taux', 'Taux %']] as [View, string][]).map(([v, l]) => (
          <button key={v} onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${view === v ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 items-end mb-4">
        <div className="flex gap-2 flex-wrap">
          {(Object.entries(PERIOD_LABELS) as [Period, string][]).map(([p, l]) => (
            <button key={p} onClick={() => { setPeriod(p); setFrom(''); setTo(''); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === p && !from ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-end ml-auto">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Du</label>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPeriod('all'); }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Au</label>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPeriod('all'); }}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-gray-400">Aucune donnée pour cette période</div>
      ) : view === 'distribution' ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="painsPrep" name="Pains préparés" fill="#fbbf24" opacity={0.6} />
            <Bar dataKey="painsDist" name="Pains distribués" fill="#d97706" />
            <Bar dataKey="vinsPrep" name="Vins préparés" fill="#a78bfa" opacity={0.6} />
            <Bar dataKey="vinsDist" name="Vins distribués" fill="#7c3aed" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v: number) => `${v}%`} />
            <Legend />
            <Line type="monotone" dataKey="tauxPains" name="Taux pains %" stroke="#d97706" strokeWidth={2} dot={{ r: 4 }} />
            <Line type="monotone" dataKey="tauxVins" name="Taux vins %" stroke="#7c3aed" strokeWidth={2} dot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}